import { addHours, startOfHour, startOfWeek, sub } from 'date-fns'

import { TimeRange, TimeScale } from 'modules/Monitoring/types'
import { getEndOfDay } from 'modules/Monitoring/utils/time/getEndOfDay'
import { getStartOfHour } from 'modules/Monitoring/utils/time/getStartOfHour'

/**
 * NOTE: This function calculates a 24-hour span (e.g., 01:00 to 00:59).
 *
 * @example
 * // If "now" is Tue Oct 21 2025 12:45:00
 * // The function centers on 12:00:00.
 * {
 * from: 1761004800000, // Tue Oct 21 2025 01:00:00 (11 hours prior)
 * to: 1761091140000,   // Wed Oct 22 2025 00:59:99 (Covers the 12th future hour)
 * }
 */
const calculatePastAndFutureTimeRange = (): TimeRange => {
  const fromValue = sub(startOfHour(Date.now()), { hours: 11 }).getTime()

  return {
    from: fromValue,
    to: addHours(sub(fromValue, { seconds: 1 }), 24).getTime(),
  }
}

export const normaliseDailyDate = (date: number | Date) => getEndOfDay(date)
export const normaliseWeeklyFromDate = (date: number | Date) =>
  sub(startOfWeek(date, { weekStartsOn: 1 }), {
    minutes: new Date().getTimezoneOffset(),
  }).getTime()
export const normaliseWeeklyToDate = (date: number | Date) => getEndOfDay(date)

export const calculateInitialTimeRangePages = ({
  timeScale,
  supportsFuture = false,
}: {
  timeScale: TimeScale
  supportsFuture?: boolean
}): TimeRange[] => {
  if (timeScale === TimeScale.Hourly) {
    const initialTimeRangePages = [
      {
        from: sub(getStartOfHour(Date.now()), { hours: 23 }).getTime(),
        to: Date.now(),
      },
    ]

    if (supportsFuture) {
      initialTimeRangePages.push(calculatePastAndFutureTimeRange())
    }

    return initialTimeRangePages
  }

  if (timeScale === TimeScale.Daily) {
    const toValue = normaliseDailyDate(Date.now())

    return [
      {
        from: sub(toValue, { days: 13 }).getTime(),
        to: toValue,
      },
    ]
  }

  return [
    {
      from: sub(normaliseWeeklyFromDate(Date.now()), { weeks: 11 }).getTime(),
      to: normaliseWeeklyToDate(Date.now()),
    },
  ]
}
